import React, { useState, useId } from 'react';

export interface UnitTextFieldProps {
  label: React.ReactNode;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  unit: string;
  fullWidth?: boolean;
  width?: number | string;
  disabled?: boolean;
}

const UnitTextField: React.FC<UnitTextFieldProps> = ({
  label,
  value,
  onChange,
  onBlur,
  unit,
  fullWidth = false,
  width,
  disabled = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputId = useId();
  const isFloating = isFocused || value !== '';

  return (
    <div
      style={{
        width: fullWidth
          ? '100%'
          : typeof width === 'number'
            ? `${width}px`
            : width,
        fontFamily: 'Roboto, sans-serif',
      }}
    >
      <div
        className={`
          relative flex items-stretch rounded-lg transition-colors duration-75
          ${disabled ? 'cursor-not-allowed opacity-[1.0] bg-[#F6F6F6]' : 'cursor-text bg-[#E5E7E9] hover:bg-neutral-85'}
        `}
        style={{
          height: 56,
          borderBottom: isFocused ? '2px solid #2a4455' : 'none',
        }}
        onClick={() => {
          if (!disabled) {
            document.getElementById(inputId)?.focus();
          }
        }}
      >
        {/* Straight bottom border — unfocused only */}
        {!isFocused && (
          <div
            className="absolute bottom-0"
            style={{
              left: '6px',
              right: '6px',
              height: '1px',
              backgroundColor: '#6a7278',
            }}
          />
        )}

        {/* Input area */}
        <div className="flex-1 relative flex flex-col justify-center px-3 py-2">
          {label && (
            <label
              htmlFor={inputId}
              className="absolute left-3 pointer-events-none origin-top-left"
              style={{
                color: isFocused ? '#2a4455' : '#474A4C',
                top: isFloating ? 4 : 20,
                fontSize: isFloating ? 12 : 16,
                lineHeight: '16px',
                transition: disabled
                  ? 'none'
                  : 'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
              }}
            >
              {label}
            </label>
          )}
          <input
            id={inputId}
            type="text"
            value={value}
            onChange={onChange}
            disabled={disabled}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              onBlur?.();
            }}
            className="w-full bg-transparent outline-none text-base leading-6 text-neutral-10 disabled:cursor-not-allowed"
            style={{
              fontSize: 16,
              lineHeight: '24px',
              paddingTop: label ? 12 : 0,
            }}
          />
        </div>

        {/* Unit section with divider */}
        <div className="flex items-center px-4 border-l border-[#474A4C]">
          <span className="text-sm text-[#474A4C]">{unit}</span>
        </div>
      </div>
    </div>
  );
};

export default UnitTextField;
